import { Group, Text } from 'react-konva';
import { IPosition2d } from '../../../../core/types/ui/position';
import { Size } from '../../../../core/types/ui/size';
import {
  AlignmentStyle,
  CheckboxValidation,
  FillStyle,
  FontStyle,
  HorizontalAlignment,
  IconSetType,
  IconSetsValidation,
  ValidationCriteria,
  ValidationRule,
} from '../../../types';
import UseKonvaImage from '../../../../core/hooks/UseKonvaImage';

import AttachmentIcon from '../../../../core/assets/icons/attachment.svg';
import CellCheckbox from './CellCheckbox';
import { getBasicTextColor } from '../../../../core/utils/view';
import ErrorIndicator from './ErrorIndicator';
import { getTextContrastColor } from '../../../helpers/cellView/cellViewHelpers';
import { UseGridOperations } from '../../../hooks/useGridOperations';
import CellIconSet from './CellIconSet';

import CloseIcon from '../../../../core/assets/icons/close.svg';
import { getIconValuesFromCategory } from '../../../helpers/cellView/iconHelpers';
import { UseKonvaCursor } from '../../../../core/hooks/useKonvaCursor';
import CanvasSVGIcon from '../../../../core/components/canvasIcons/CanvasSVGIcon';
import {
  isValidCheckboxValue,
  isValidIconSetValue,
} from '../../../helpers/cellView/cellValidationRule';

type CellVisibleStyle = Omit<FontStyle, 'color'> & AlignmentStyle;
//   Partial<Pick<Style, 'textWrap' | 'rotation'>>;

interface CellContentsProps
  extends UseKonvaCursor,
    Pick<UseGridOperations, 'performSheetOperation'>,
    Size,
    IPosition2d,
    CellVisibleStyle {
  sheetId: string;
  cellValue: string;
  formattedValue: string;
  dataValidation?: ValidationRule;
  ri: number;
  ci: number;
  isRtl: boolean;
  padding: number;
  size: number;
  family: string;
  color?: string | null;
  backgroundColor?: string | null;
  zoom: number;
  isDarkTheme: boolean;
  isError: boolean;
}
const CellContents = ({
  x,
  y,
  dataValidation,
  cellValue,
  formattedValue,
  ri,
  ci,
  width,
  height,
  bold,
  italic,
  strikethrough,
  underline,
  isRtl,
  padding,
  horizontal,
  vertical,
  size,
  family,
  color,
  zoom,
  backgroundColor,
  isDarkTheme,
  isError,
  sheetId,
  performSheetOperation,
  onMouseEnter,
  onMouseLeave,
}: CellContentsProps) => {
  const visibleColor = color ?? getBasicTextColor(isDarkTheme);

  // const isCheckboxErrorCase = (
  //   value: string,
  //   checkedValue: string,
  //   uncheckedValue: string
  // ) => !!(value.length && value !== checkedValue && value !== uncheckedValue);

  const renderTextGroup = (error?: boolean) => {
    const fontStyle = `${bold ? 'bold' : ''} ${italic ? 'italic' : ''}`;
    const decoration = `${strikethrough ? 'line-through' : ''} ${
      underline ? 'underline' : ''
    } `;
    return (
      <Group>
        <Group x={x + padding} y={y + padding}>
          <Text
            width={width - 2 * padding}
            height={height - 2 * padding}
            align={horizontal ?? HorizontalAlignment.Left}
            verticalAlign={vertical ?? 'middle'}
            fontSize={size ?? 14}
            fontFamily={family}
            text={formattedValue}
            fill={visibleColor}
            fontStyle={fontStyle}
            textDecoration={decoration}
            wrap={'word'}
          />
        </Group>
        {(isError || error) && (
          <ErrorIndicator zoom={zoom} width={width} x={x} y={y} />
        )}
      </Group>
    );
  };

  //   const dropdownIcon = () => {
  //     const elSize = size
  //     const right = width - elSize
  //     const top = (height - elSize) / 2

  //     return (
  //         <Group>
  //         <Rect />
  //         </Group>
  //     )
  //   }

  const renderAttachmentIcon = () => {
    const { primaryColor } = getTextContrastColor(
      backgroundColor,
      color,
      isDarkTheme
    );
    const iconSize = 20 * zoom;
    const iconY = (height - iconSize) / 2;
    const iconX = width - iconSize - padding * 2;
    return (
      <Group>
        {renderTextGroup()}
        <Group x={x} y={y}>
          <CanvasSVGIcon
            x={iconX}
            y={iconY}
            height={iconSize}
            width={iconSize}
            fill={primaryColor}
            viewBoxSize={32}
            paths={[
              'M0 16v-1.984q0-3.328 2.336-5.664t5.664-2.336q1.024 0 2.176 0.352 0.576-2.752 2.784-4.544t5.056-1.824q3.296 0 5.632 2.368t2.368 5.632q0 0.896-0.32 2.048 0.224-0.032 0.32-0.032 2.464 0 4.224 1.76t1.76 4.224v2.016q0 2.496-1.76 4.224t-4.224 1.76h-0.384q0.288-0.8 0.352-1.44 0.096-1.312-0.32-2.56t-1.408-2.208l-4-4q-1.76-1.792-4.256-1.792t-4.224 1.76l-4 4q-0.96 0.96-1.408 2.24t-0.32 2.592q0.032 0.576 0.256 1.248-2.72-0.608-4.512-2.784t-1.792-5.056zM10.016 22.208q-0.096-0.96 0.576-1.6l4-4q0.608-0.608 1.408-0.608 0.832 0 1.408 0.608l4 4q0.672 0.64 0.608 1.6-0.032 0.288-0.16 0.576-0.224 0.544-0.736 0.896t-1.12 0.32h-1.984v6.016q0 0.832-0.608 1.408t-1.408 0.576-1.408-0.576-0.576-1.408v-6.016h-2.016q-0.608 0-1.088-0.32t-0.768-0.896q-0.096-0.288-0.128-0.576z',
            ]}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={() => {}}
          />
        </Group>
      </Group>
    );
  };

  const renderCellCheckbox = (rule: CheckboxValidation) => {
    const { isValid, isChecked, checkedString, uncheckedString } =
      isValidCheckboxValue(rule, cellValue);
    if (!isValid) return <Group>{renderTextGroup(true)}</Group>;

    return (
      <CellCheckbox
        size={size}
        backgroundColor={backgroundColor}
        color={color}
        width={width}
        height={height}
        x={x}
        y={y}
        isDarkTheme={isDarkTheme}
        position={{
          ci,
          ri,
        }}
        isChecked={isChecked}
        performSheetOperation={performSheetOperation}
        checkedValue={checkedString}
        uncheckedValue={uncheckedString}
        sheetId={sheetId}
        // onMouseEnter={onMouseEnter}
        // onMouseLeave={onMouseLeave}
      />
    );
  };

  const renderCellIconSets = (dataValidation: IconSetsValidation) => {
    const { isValid } = isValidIconSetValue(dataValidation, cellValue);

    if (!isValid) return <Group>{renderTextGroup(true)}</Group>;
    return (
      <CellIconSet
        x={x}
        y={y}
        width={width}
        height={height}
        rule={dataValidation}
        sheetId={sheetId}
        size={size}
        backgroundColor={backgroundColor}
        color={color}
        isDarkTheme={isDarkTheme}
        zoom={zoom}
        cellValue={cellValue}
        ci={ci}
        ri={ri}
        performSheetOperation={performSheetOperation}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    );
  };

  //   const textWithDropdown = () => {
  //     return <Group>{text()}</Group>;
  //   };

  if (dataValidation) {
    switch (dataValidation.criteria) {
      //   case ValidationCriteria.CellRange:
      //   case ValidationCriteria.List:
      //     return textWithDropdown();
      case ValidationCriteria.Files:
        return renderAttachmentIcon();
      case ValidationCriteria.Checkbox:
        return renderCellCheckbox(dataValidation);
      case ValidationCriteria.IconSets:
        return renderCellIconSets(dataValidation);
    }
  }

  return renderTextGroup();
};

export default CellContents;
